import React from 'react';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import CoordInput from '../../molecules/CoordInput';
import { fieldId, fieldLabel, isWrappable } from './placeholderFields';
import { DEFAULT_MAX_LINES } from '../../utils/formSetupUtils';
import { FORM_SETUP_FONT_SIZE } from '../../__DATA__';

// One step = one view pixel; Shift = 10. The canvas is rendered at 72dpi so a
// pixel here is a point on the page.
const STEP_PX = 1;
const COARSE_STEP_PX = 10;

interface Props {
  fields: any[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (field: any, patch: { x?: number; y?: number; width?: number; height?: number }) => void;
  onDelete: (field: any) => void;
  /**
   * Sets a placeholder's sample value for Preview PDF. Separate from onMove
   * on purpose — onMove invalidates the stored coordinates, and typing a test
   * value must not move anything.
   */
  onPreviewTextChange?: (field: any, value: string) => void;
  /**
   * Sets a placeholder's print options — currently just wrapping. Separate
   * from onMove for the same reason as onPreviewTextChange: onMove clears the
   * stored coordinates, and ticking a checkbox must not move anything.
   */
  onOptionChange?: (
    field: any,
    patch: { multiline?: boolean; maxLines?: string; fontSize?: string },
  ) => void;
  /**
   * The form-wide Font Size, in raster pixels — only to label the "Same as
   * form" option with the size it actually means. Not a fallback the panel
   * applies to anything.
   */
  formFontPx?: number;
}

// The renderer draws every other field as ONE run hanging off the X/Y corner,
// so saying so is the honest thing (form_setup/pdf_render.py).
const NO_WRAP_HINT =
  'The box size is saved but does not affect the printed form — text is ' +
  'drawn from the X/Y corner and is not wrapped or clipped.';

// Per-placeholder settings, modelled on the Inspection Form Builder's
// FieldPropertiesPanel + FieldEditorCard.
const PlaceholderPropertiesPanel = ({
  fields,
  selectedId,
  onSelect,
  onMove,
  onDelete,
  onPreviewTextChange,
  onOptionChange,
  formFontPx,
}: Props) => {
  const [showAll, setShowAll] = React.useState(false);

  if (!fields.length) {
    return (
      <Wrapper>
        <Empty>
          Pick a field on the right to place it on the form, then drag it into
          position or type its coordinates here.
        </Empty>
      </Wrapper>
    );
  }

  const visible = showAll
    ? fields
    : fields.filter(field => fieldId(field) === selectedId);

  return (
    <Wrapper>
      <PanelHead>
        <Eyebrow>Placed fields ({fields.length})</Eyebrow>
        <ToggleAll type="button" onClick={() => setShowAll(prev => !prev)}>
          {showAll ? 'Show selected only' : 'Show all'}
        </ToggleAll>
      </PanelHead>

      {visible.length === 0 && (
        <Empty>Select a field on the form to edit its position.</Empty>
      )}

      {visible.map(field => {
        const id = fieldId(field);
        return (
          <Card
            key={id}
            $selected={id === selectedId}
            onMouseDown={() => onSelect(id)}
          >
            <CardHead>
              <CardTitle title={fieldLabel(field)}>{fieldLabel(field)}</CardTitle>
              <IconBtn
                type="button"
                onClick={() => onDelete(field)}
                aria-label={`Remove ${fieldLabel(field)}`}
                title="Remove this placeholder"
              >
                <DeleteIcon style={{ fontSize: 18 }} />
              </IconBtn>
            </CardHead>

            {onPreviewTextChange && (
              <Field>
                <Lbl>Test value (Preview PDF only)</Lbl>
                <Input
                  value={field.previewText ?? ''}
                  placeholder={`e.g. a sample ${fieldLabel(field)}`}
                  onChange={e =>
                    onPreviewTextChange(field, e.target.value)}
                />
              </Field>
            )}

            <Two>
              <CoordInput
                axis="x"
                label="X (px)"
                unit="px"
                step={STEP_PX}
                coarseStep={COARSE_STEP_PX}
                value={field.x}
                onChange={v => onMove(field, { x: Number(v) || 0 })}
              />
              <CoordInput
                axis="y"
                label="Y (px)"
                unit="px"
                step={STEP_PX}
                coarseStep={COARSE_STEP_PX}
                value={field.y}
                onChange={v => onMove(field, { y: Number(v) || 0 })}
              />
            </Two>

            <Two>
              <Field>
                <Lbl>Width</Lbl>
                <Input
                  value={field.width ?? ''}
                  inputMode="numeric"
                  onChange={e => onMove(field, { width: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field>
                <Lbl>Height</Lbl>
                <Input
                  value={field.height ?? ''}
                  inputMode="numeric"
                  onChange={e => onMove(field, { height: Number(e.target.value) || 0 })}
                />
              </Field>
            </Two>
            {/* Wrapping is offered on Information only — see isWrappable. For
                every other placeholder the hint stays what it always was:
                being straight with the user rather than implying it wraps. */}
            {onOptionChange && isWrappable(field) ? (
              <>
                <CheckRow>
                  <input
                    type="checkbox"
                    checked={!!field.multiline}
                    onChange={e =>
                      onOptionChange(field, {
                        multiline: e.target.checked,
                      })}
                  />
                  <span>Wrap long text onto multiple lines</span>
                </CheckRow>
                {field.multiline && (
                  <Field>
                    <Lbl>Max lines</Lbl>
                    <Input
                      value={field.maxLines ?? ''}
                      inputMode="numeric"
                      placeholder={String(DEFAULT_MAX_LINES)}
                      onChange={e =>
                        onOptionChange(field, {
                          // Digits only, and blank is allowed — an empty box
                          // means "use the default", not zero lines.
                          maxLines: e.target.value.replace(/[^0-9]/g, ''),
                        })}
                    />
                  </Field>
                )}
                {/* Offered whether or not it wraps: a long comment needs a
                    smaller size than the rest of the form either way. Shown
                    here rather than beside the form's Font Size picker so it
                    is obvious the setting belongs to THIS placeholder. */}
                <Field>
                  <Lbl>Font size</Lbl>
                  <Select
                    value={field.fontSize ?? ''}
                    onChange={e =>
                      onOptionChange(field, {
                        fontSize: e.target.value,
                      })}
                  >
                    <option value="">
                      {formFontPx
                        ? `Same as form (${formFontPx} px)`
                        : 'Same as form'}
                    </option>
                    {FORM_SETUP_FONT_SIZE.map((item: any) => (
                      <option key={item.value} value={item.value}>
                        {item.label} px
                      </option>
                    ))}
                  </Select>
                </Field>
                {field.multiline ? (
                  <Hint>
                    Wraps inside the <b>Width</b> above, then shrinks if it
                    still needs more than Max lines. <b>Height</b> follows Max
                    lines and the <b>Font size</b> above, so the box shows how
                    far the text can reach — leave blank space under it.
                    Anything past the last line is not printed.
                  </Hint>
                ) : (
                  <Hint>{NO_WRAP_HINT}</Hint>
                )}
              </>
            ) : (
              <Hint>{NO_WRAP_HINT}</Hint>
            )}
          </Card>
        );
      })}
    </Wrapper>
  );
};

export default PlaceholderPropertiesPanel;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: ${FONT_STACK};
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const ToggleAll = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-700);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const Card = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--primary-600)' : 'var(--border)')};
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: ${({ $selected }) => ($selected ? 'var(--shadow-focus)' : 'var(--shadow-xs)')};
  box-sizing: border-box;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Lbl = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

// A native <select> rather than the app's InputSelectWithLabel: this panel sits
// inside a draggable modal, and react-select renders its menu in a portal that
// the modal's own overflow then clips.
const Select = styled.select`
  height: 36px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
  line-height: 1.4;

  b {
    color: var(--neutral-600);
  }
`;

// Semantic <label> so the whole row toggles, matching the Threshold report
// builder's own multiline switch (PreviewTool/builder/FieldEditorCard.tsx).
const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
  cursor: pointer;

  input {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
    accent-color: var(--primary-600);
    cursor: pointer;
  }
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--neutral-500);
  line-height: 1.5;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--danger-50, #fdecea);
    border-color: var(--danger-500, #d44b3f);
    color: var(--danger-500, #d44b3f);
  }
`;
